import { useState, useEffect, useMemo, useRef, useCallback } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

// ─── Schema ───────────────────────────────────────────────────────────────
const TAILOR_FIELD_GROUPS = [
  {
    title: "Upper Body",
    fields: [
      { key: "shoulder",     label: "Shoulder",     default: 14, sane: [10, 25] },
      { key: "chest",        label: "Chest",        default: 36, sane: [22, 60] },
      { key: "waist",        label: "Waist",        default: 28, sane: [18, 60] },
      { key: "hip",          label: "Hip",          default: 38, sane: [22, 60] },
      { key: "sleeveLength", label: "Sleeve Len",   default: 22, sane: [12, 30] },
      { key: "sleeveCircum", label: "Sleeve Round", default: 11, sane: [6, 22]  },
      { key: "bicep",        label: "Bicep",        default: 12, sane: [6, 22]  },
      { key: "armhole",      label: "Armhole",      default: 16, sane: [10, 28] },
    ],
  },
  {
    title: "Lower Body",
    fields: [
      { key: "bottomWaist",  label: "Pant Waist",   default: 30, sane: [18, 60] },
      { key: "bottomHip",    label: "Pant Hip",     default: 38, sane: [22, 60] },
      { key: "bottomThigh",  label: "Pant Thigh",   default: 22, sane: [12, 40] },
      { key: "bottomKnee",   label: "Pant Knee",    default: 16, sane: [10, 30] },
      { key: "bottomLength", label: "Pant Length",  default: 40, sane: [20, 50] },
    ],
  },
  {
    title: "Other",
    fields: [
      { key: "neck",   label: "Neck",   default: 14, sane: [10, 22] },
      { key: "length", label: "Length", default: 40, sane: [14, 60] },
    ],
  },
];

// Jewellery sizing — 4 fields, stored in mm (except ring size which is unitless 1-30)
const JEWELLERY_FIELD_GROUPS = [
  {
    title: "Jewellery Sizing",
    fields: [
      { key: "ringSize",     label: "Ring Size",     default: 14, sane: [1, 30],   unitOverride: "" },
      { key: "wristCircum",  label: "Wrist",         default: 58, sane: [40, 90]  },
      { key: "neckCircum",   label: "Neck",          default: 35, sane: [25, 55]  },
      { key: "fingerLength", label: "Finger Length", default: 70, sane: [45, 100] },
    ],
  },
];

function getFieldGroups(vertical) {
  if (vertical === "jewellery" || vertical === "jewelry") return JEWELLERY_FIELD_GROUPS;
  return TAILOR_FIELD_GROUPS;
}

const FIELD_GROUPS = TAILOR_FIELD_GROUPS; // kept for backward compat where needed

const ALL_FIELDS    = TAILOR_FIELD_GROUPS.flatMap(g => g.fields);
const ALL_FIELD_KEYS = ALL_FIELDS.map(f => f.key);
const FIELD_BY_KEY  = Object.fromEntries(ALL_FIELDS.map(f => [f.key, f]));

const TAG_OPTIONS = ["regular", "bridal", "premium", "new"];

// ─── Helpers ──────────────────────────────────────────────────────────────
const emptyMeasurements = () => ({
  ...Object.fromEntries(ALL_FIELD_KEYS.map(k => [k, null])),
  notes: "",
});

const emptyCustomer = () => ({
  name: "", phone: "", notes: "",
  tags: [], measurementUnit: "in",
  measurements: emptyMeasurements(),
});

function getDefault(key, unit, cDefaults) {
  const f = FIELD_BY_KEY[key];
  if (!f) return 0;
  const inches = (cDefaults && cDefaults[key] != null) ? Number(cDefaults[key]) : f.default;
  return unit === "cm" ? Math.round(inches * 2.54) : inches;
}

function buildDefaultMeasurements(unit, cDefaults, vertical) {
  const groups = getFieldGroups(vertical);
  const keys   = groups.flatMap(g => g.fields.map(f => f.key));
  const r = { notes: "" };
  keys.forEach(k => { r[k] = getDefault(k, unit, cDefaults); });
  return r;
}

function isOutlier(key, value, unit) {
  const f = FIELD_BY_KEY[key];
  if (!f || value == null || value === "") return false;
  const v = Number(value);
  const [mn, mx] = unit === "cm" ? [f.sane[0] * 2.54, f.sane[1] * 2.54] : f.sane;
  return v < mn || v > mx;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  const days = Math.floor(d / 86400);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function countFilled(m, vertical) {
  if (!m) return 0;
  const groups = getFieldGroups(vertical);
  const keys   = groups.flatMap(g => g.fields.map(f => f.key));
  return keys.filter(k => m[k] != null && m[k] !== "").length;
}

function totalFields(vertical) {
  return getFieldGroups(vertical).flatMap(g => g.fields).length;
}

// ─── Collapsing Tags Component ─────────────────────────────────────────────
// Shows all options when none selected. Once one is chosen, shows only
// selected tag(s) + a small "edit" chip to expand again.
function TagSelector({ tags, onChange }) {
  const [expanded, setExpanded] = useState(tags.length === 0);

  const toggle = (tag) => {
    const next = tags.includes(tag)
      ? tags.filter(t => t !== tag)
      : [...tags, tag];
    onChange(next);
    // If we just deselected everything, re-expand so tailor can pick
    if (next.length === 0) setExpanded(true);
    // If we just selected something and were expanded, collapse
    else if (!tags.includes(tag)) setExpanded(false);
  };

  const hasTags = tags.length > 0;

  if (!expanded && hasTags) {
    // Collapsed state: show selected chips + edit button
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        {tags.map(t => (
          <span key={t} style={st.tagChipSelected(t)}>✓ {t}</span>
        ))}
        <button
          onClick={() => setExpanded(true)}
          style={st.tagEditBtn}
        >
          Edit
        </button>
      </div>
    );
  }

  // Expanded: show all options
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {TAG_OPTIONS.map(tag => (
        <button
          key={tag}
          onClick={() => toggle(tag)}
          style={{
            ...st.tagBtn,
            ...(tags.includes(tag) ? st.tagBtnActive(tag) : {}),
          }}
        >
          {tags.includes(tag) ? "✓ " : ""}{tag}
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function CustomersTab({ business, catalogueDefaults, vertical }) {
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [selected, setSelected]     = useState(null);
  const [showAdd, setShowAdd]       = useState(false);
  const [error, setError]           = useState("");

  const businessId = business?.businessId;
  const hasCustomDefaults = useMemo(() =>
    !!(catalogueDefaults && Object.keys(catalogueDefaults).length > 0),
  [catalogueDefaults]);

  const loadCustomers = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/customers?businessId=${businessId}`);
      const d   = await res.json();
      setCustomers(d.customers || []);
    } catch { setError("Could not load customers"); }
    finally  { setLoading(false); }
  }, [businessId]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...customers]
      .filter(c => !q ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q))
      .sort((a, b) => {
        const da = a.updatedAt || a.createdAt || "";
        const db = b.updatedAt || b.createdAt || "";
        return db.localeCompare(da);
      });
  }, [customers, search]);

  const saveCustomer = async (customer, isNew) => {
    const res = await fetch(`${API}/customers`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...customer, businessId }),
    });
    if (!res.ok) throw new Error((await res.text()) || "Save failed");
    const d = await res.json();
    return d.customer || { ...customer, businessId, customerId: d.customerId };
  };

  return (
    <div style={{ paddingBottom: 100 }}>
      <div style={st.header}>
        <div>
          <div style={st.title}>Customers</div>
          <div style={st.subtitle}>{customers.length} saved</div>
        </div>
        <button style={st.addBtn} onClick={() => setShowAdd(true)}>+ Add</button>
      </div>

      <div style={{ padding: "0 16px 12px" }}>
        <input
          type="text" inputMode="search"
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or phone…" style={st.search}
        />
      </div>

      {loading ? (
        <div style={st.empty}>Loading…</div>
      ) : sorted.length === 0 ? (
        <div style={st.empty}>
          {search ? "No customers match." : "No customers yet. Tap + Add to create one."}
        </div>
      ) : sorted.map(c => (
        <CustomerCard key={c.customerId} customer={c} vertical={vertical} onTap={() => setSelected(c)} />
      ))}

      {error && <div style={st.errorBar}>{error}</div>}

      {selected && (
        <CustomerDrawer
          customer={selected}
          catalogueDefaults={catalogueDefaults}
          hasCustomDefaults={hasCustomDefaults}
          vertical={vertical}
          onClose={() => setSelected(null)}
          onSave={async (updated) => {
            const saved = await saveCustomer(updated, false);
            setCustomers(prev => prev.map(x =>
              x.customerId === saved.customerId ? { ...x, ...saved } : x));
            setSelected(null);
          }}
        />
      )}

      {showAdd && (
        <AddCustomerModal
          catalogueDefaults={catalogueDefaults}
          hasCustomDefaults={hasCustomDefaults}
          vertical={vertical}
          onClose={() => setShowAdd(false)}
          onCreate={async (newCustomer, mode) => {
            const saved = await saveCustomer(newCustomer, true);
            setCustomers(prev => [saved, ...prev]);
            setShowAdd(false);
            if (mode === "quick") setSelected(saved);
          }}
        />
      )}
    </div>
  );
}

// ─── Customer Row Card ─────────────────────────────────────────────────────
function CustomerCard({ customer, vertical, onTap }) {
  const filled = countFilled(customer.measurements || {}, vertical);
  const total  = totalFields(vertical);
  const tagsList = customer.tags || [];
  return (
    <div style={st.card} onClick={onTap}>
      <div style={st.avatar}>
        {(customer.name || "?").trim().charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={st.cardName}>{customer.name || "Unnamed"}</div>
        <div style={st.cardPhone}>{customer.phone || "—"}</div>
        <div style={st.cardMeta}>
          {filled > 0
            ? <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ {filled}/{total} measurements</span>
            : <span style={{ color: "#aaa" }}>No measurements yet</span>}
          {customer.measurementsUpdatedAt && filled > 0 &&
            <span style={{ color: "#aaa" }}> · {timeAgo(customer.measurementsUpdatedAt)}</span>}
        </div>
        {tagsList.length > 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
            {tagsList.map(t => <span key={t} style={st.tagChip(t)}>{t}</span>)}
          </div>
        )}
      </div>
      <div style={{ color: "#bbb", fontSize: 22 }}>›</div>
    </div>
  );
}

// ─── Customer Drawer ───────────────────────────────────────────────────────
function CustomerDrawer({ customer, catalogueDefaults, hasCustomDefaults, vertical, onClose, onSave }) {
  const isNewCustomer = useMemo(() => countFilled(customer.measurements || {}, vertical) === 0,
    [customer.measurements]);

  // Jewellery customers default to mm, tailor to inches
  const defaultUnit = (vertical === "jewellery" || vertical === "jewelry") ? "mm" : "in";

  const initialMeasurements = useMemo(() =>
    isNewCustomer
      ? buildDefaultMeasurements(customer.measurementUnit || defaultUnit, catalogueDefaults, vertical)
      : { ...emptyMeasurements(), ...(customer.measurements || {}) },
  [isNewCustomer, customer.measurementUnit, customer.measurements, catalogueDefaults]);

  const initial = useMemo(() => ({
    ...emptyCustomer(), ...customer,
    measurementUnit: customer.measurementUnit || defaultUnit,
    tags: customer.tags || [],
    measurements: initialMeasurements,
  }), [customer, initialMeasurements]);

  const [form, setForm]             = useState(initial);
  const [activeField, setActiveField] = useState(null);
  const [collapsed, setCollapsed]   = useState({});
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState("");
  const [history, setHistory]       = useState([]);
  const [shake, setShake]           = useState(false);
  const [bannerVisible, setBannerVisible] = useState(isNewCustomer);

  const initialRef = useRef(JSON.stringify(initial));
  const dirty      = JSON.stringify(form) !== initialRef.current;
  const fieldGroups  = getFieldGroups(vertical);
  const fieldKeys    = fieldGroups.flatMap(g => g.fields.map(f => f.key));
  const filledCount  = countFilled(form.measurements, vertical);
  const totalCount   = totalFields(vertical);

  // Auto-dismiss confirmation banner after 4s (configured tailors only)
  useEffect(() => {
    if (isNewCustomer && hasCustomDefaults) {
      const t = setTimeout(() => setBannerVisible(false), 4000);
      return () => clearTimeout(t);
    }
  }, [isNewCustomer, hasCustomDefaults]);

  const updateMeasurement = (key, val) => {
    setHistory(h => [...h.slice(-9), { ...form.measurements }]);
    setForm(f => ({ ...f, measurements: { ...f.measurements, [key]: val } }));
  };

  const undo   = () => {
    if (!history.length) return;
    const last = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setForm(f => ({ ...f, measurements: last }));
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Stepper on card: ±0.5 without opening keypad
  const stepField = (key, delta) => {
    const cur = form.measurements[key];
    if (cur == null || cur === "") return;
    const next = Math.round((parseFloat(cur) + delta) * 10) / 10;
    if (next < 0 || next > 99) return;
    updateMeasurement(key, next);
  };

  // Keypad adjust chips (in header)
  const adjustActive = (delta) => {
    if (!activeField) return;
    stepField(activeField, delta);
  };

  const clearActive = () => {
    if (!activeField) return;
    setHistory(h => [...h.slice(-9), { ...form.measurements }]);
    setForm(f => ({ ...f, measurements: { ...f.measurements, [activeField]: null } }));
  };

  const advanceToNext = () => {
    if (!activeField) return;
    const idx  = fieldKeys.indexOf(activeField);
    const next = fieldKeys[idx + 1];
    if (next) {
      setActiveField(next);
      const grp = fieldGroups.find(g => g.fields.some(f => f.key === next));
      if (grp && collapsed[grp.title]) setCollapsed(c => ({ ...c, [grp.title]: false }));
    } else setActiveField(null);
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 250);
  };

  const handleKeypadInput = (digit) => {
    if (!activeField) return;
    const cur    = form.measurements[activeField];
    const curStr = cur == null ? "" : String(cur);
    let next;
    if (digit === "⌫") {
      next = curStr.slice(0, -1);
      if (next.endsWith(".")) next = next.slice(0, -1);
    } else if (digit === ".5") {
      if (curStr.endsWith(".5"))         next = curStr.slice(0, -2);
      else if (curStr.includes("."))     next = curStr.split(".")[0] + ".5";
      else if (curStr === "")            next = "0.5";
      else                               next = curStr + ".5";
    } else {
      const intPart = curStr.includes(".") ? curStr.split(".")[0] : curStr;
      if (!curStr.includes(".") && intPart.length >= 2) { triggerShake(); return; }
      next = curStr.includes(".")
        ? curStr.split(".")[0] + "." + digit
        : curStr + digit;
    }
    const parsed = next === "" ? null : parseFloat(next);
    if (next !== "" && (isNaN(parsed) || parsed > 99)) { triggerShake(); return; }
    updateMeasurement(activeField, next === "" ? null : parsed);
  };

  const handleSave = async () => {
    setSaving(true); setSaveError("");
    try   { await onSave(form); }
    catch (e) { setSaveError(e.message || "Save failed"); setSaving(false); }
  };

  return (
    <div style={st.drawerBackdrop} onClick={onClose}>
      <div style={st.drawer} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={st.drawerHeader}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Customer · {filledCount}/{totalCount} measurements
            </div>
            <input value={form.name} onChange={e => setField("name", e.target.value)}
              placeholder="Name" style={st.headerInput} />
            <input value={form.phone} onChange={e => setField("phone", e.target.value)}
              placeholder="Phone" inputMode="tel"
              style={{ ...st.headerInput, fontSize: 13, color: "#666", marginTop: 2 }} />
          </div>
          <button onClick={onClose} style={st.closeBtn}>✕</button>
        </div>

        {/* Banner */}
        {bannerVisible && isNewCustomer && (
          <div style={hasCustomDefaults ? st.bannerSuccess : st.bannerEducational}>
            <span>
              {hasCustomDefaults
                ? "✨ Pre-filled with your saved defaults · Edit any field"
                : "💡 Pre-filled with general defaults · Set your shop's averages in Settings"}
            </span>
            <button onClick={() => setBannerVisible(false)} style={st.bannerDismiss}>✕</button>
          </div>
        )}

        {/* Tags + controls */}
        <div style={{ padding: "0 16px 12px", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ marginBottom: 10 }}>
            <TagSelector
              tags={form.tags}
              onChange={tags => setForm(f => ({ ...f, tags }))}
            />
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", background: "#f5f5f3", borderRadius: 10, padding: 3 }}>
              <button onClick={() => setField("measurementUnit", "in")}
                style={{ ...st.unitBtn, ...(form.measurementUnit === "in" ? st.unitBtnActive : {}) }}>
                inches
              </button>
              <button onClick={() => setField("measurementUnit", "cm")}
                style={{ ...st.unitBtn, ...(form.measurementUnit === "cm" ? st.unitBtnActive : {}) }}>
                cm
              </button>
            </div>
            {history.length > 0 && (
              <button onClick={undo} style={st.undoBtn}>↩ Undo</button>
            )}
          </div>
        </div>

        {/* Field grid — scrollable */}
        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 16 }}>
          {fieldGroups.map(group => (
            <MeasurementGroup
              key={group.title}
              group={group}
              measurements={form.measurements}
              unit={form.measurementUnit}
              activeField={activeField}
              setActiveField={setActiveField}
              onStep={stepField}
              collapsed={collapsed[group.title]}
              toggleCollapse={() => setCollapsed(c => ({ ...c, [group.title]: !c[group.title] }))}
            />
          ))}

          <div style={{ padding: "12px 16px 4px" }}>
            <div style={st.fieldLabel}>Fit Notes</div>
            <textarea
              value={form.measurements.notes || ""}
              onChange={e => setForm(f => ({ ...f, measurements: { ...f.measurements, notes: e.target.value } }))}
              placeholder="e.g. Loose fit, princess seams, hooks not zip…"
              rows={2} style={st.textarea}
            />
          </div>

          <div style={{ padding: "12px 16px 80px" }}>
            <div style={st.fieldLabel}>Customer Notes</div>
            <textarea
              value={form.notes || ""}
              onChange={e => setField("notes", e.target.value)}
              placeholder="e.g. Wedding 15 Dec — needs lehenga + 2 reception outfits"
              rows={2} style={st.textarea}
            />
          </div>
        </div>

        {/* Keypad */}
        {activeField && (
          <Keypad
            field={FIELD_BY_KEY[activeField]}
            value={form.measurements[activeField]}
            unit={form.measurementUnit}
            shake={shake}
            onInput={handleKeypadInput}
            onClose={() => setActiveField(null)}
            onClear={clearActive}
            onAdjust={adjustActive}
            onNext={advanceToNext}
            isLast={fieldKeys.indexOf(activeField) === fieldKeys.length - 1}
          />
        )}

        {saveError && (
          <div style={{ padding: "8px 16px", background: "#fff0f0", color: "#dc2626", fontSize: 13 }}>
            {saveError}
          </div>
        )}

        <div style={st.drawerFooter}>
          {!dirty
            ? <button onClick={onClose} style={st.closeFullBtn}>Close</button>
            : <>
                <button onClick={onClose} style={st.cancelBtn}>Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                  style={{ ...st.saveBtn, opacity: (saving || !form.name.trim()) ? 0.4 : 1 }}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Measurement Group ─────────────────────────────────────────────────────
function MeasurementGroup({ group, measurements, unit, activeField, setActiveField, onStep, collapsed, toggleCollapse }) {
  const filledCount = group.fields.filter(f =>
    measurements[f.key] != null && measurements[f.key] !== "").length;

  return (
    <div style={{ marginTop: 4 }}>
      <div onClick={toggleCollapse} style={st.groupHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 0.5 }}>
            {group.title}
          </span>
          <span style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>
            {filledCount}/{group.fields.length}
          </span>
        </div>
        <span style={{ fontSize: 16, color: "#aaa" }}>{collapsed ? "▸" : "▾"}</span>
      </div>

      {!collapsed && (
        <div style={st.fieldGrid}>
          {group.fields.map(field => (
            <MeasurementField
              key={field.key}
              field={field}
              value={measurements[field.key]}
              unit={unit}
              isActive={activeField === field.key}
              isOutlier={isOutlier(field.key, measurements[field.key], unit)}
              onTapValue={() => setActiveField(field.key)}
              onStep={(delta) => onStep(field.key, delta)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Measurement Field — with stepper chips on filled cards ────────────────
function MeasurementField({ field, value, unit, isActive, isOutlier, onTapValue, onStep }) {
  const filled     = value != null && value !== "";
  const displayVal = filled ? String(value) : "—";

  let cardStyle = { ...st.fieldCard };
  if (isActive)                   cardStyle = { ...cardStyle, ...st.fieldCardActive };
  else if (isOutlier && filled)   cardStyle = { ...cardStyle, ...st.fieldCardOutlier };
  else if (filled)                cardStyle = { ...cardStyle, ...st.fieldCardFilled };

  // Long-press → manual prompt for outlier/special values
  const longPressTimer    = useRef(null);
  const longPressTriggered = useRef(false);

  const handlePointerDown = () => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      const input = window.prompt(`${field.label} (${unit}):`, value ?? "");
      if (input != null) {
        const n = parseFloat(input.trim());
        if (!isNaN(n) && n >= 0 && n <= 99) onStep(n - (value ?? 0)); // delta to reach n
      }
    }, 600);
  };
  const handlePointerUp = () => { clearTimeout(longPressTimer.current); };

  const handleValueTap = (e) => {
    if (longPressTriggered.current) { e.stopPropagation(); return; }
    onTapValue();
  };

  return (
    <div style={cardStyle}>
      {/* Label row */}
      <div style={st.fieldLabelInline}>
        {field.label}
        {isOutlier && filled && <span style={{ color: "#d97706", marginLeft: 3 }}>⚠</span>}
      </div>

      {filled ? (
        /* Filled: value centre + stepper chips either side */
        <div style={st.fieldStepperRow}>
          <button
            onClick={(e) => { e.stopPropagation(); onStep(-0.5); }}
            style={st.cardStepBtn}
            aria-label={`Decrease ${field.label}`}
          >
            −
          </button>

          <div
            style={{ ...st.fieldValue, color: "#111", flex: 1, textAlign: "center", cursor: "pointer" }}
            onClick={handleValueTap}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            {displayVal}
            <span style={st.fieldUnit}>{unit}</span>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onStep(0.5); }}
            style={st.cardStepBtn}
            aria-label={`Increase ${field.label}`}
          >
            +
          </button>
        </div>
      ) : (
        /* Empty: just tap to open keypad */
        <div
          style={{ ...st.fieldValue, color: "#bbb", textAlign: "center", padding: "10px 0", cursor: "pointer" }}
          onClick={handleValueTap}
        >
          —
        </div>
      )}
    </div>
  );
}

// ─── Keypad ────────────────────────────────────────────────────────────────
function Keypad({ field, value, unit, shake, onInput, onClose, onClear, onAdjust, onNext, isLast }) {
  const keys     = ["1","2","3","4","5","6","7","8","9",".5","0","⌫"];
  const hasValue = value != null && value !== "";

  return (
    <div style={st.keypad}>
      <style>{`
        @keyframes qkardShake {
          0%,100% { transform:translateX(0); }
          25%      { transform:translateX(-4px); }
          75%      { transform:translateX(4px); }
        }
      `}</style>

      {/* Value display row with +/- chips */}
      <div style={st.keypadHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Editing
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            animation: shake ? "qkardShake 0.25s ease-in-out" : "none",
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#111", flexShrink: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {field?.label || ""}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600, color: shake ? "#dc2626" : "#666", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
              · {value ?? "—"}{value != null ? ` ${unit}` : ""}
            </span>
            {hasValue && (
              <>
                <button onClick={() => onAdjust(-0.5)} style={st.adjustChip}>−</button>
                <button onClick={() => onAdjust(0.5)}  style={st.adjustChip}>+</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action row */}
      <div style={st.keypadActions}>
        <button onClick={onClear} style={st.keypadClear}>Clear</button>
        <button onClick={onClose} style={st.keypadDone}>Done</button>
        {!isLast && <button onClick={onNext} style={st.keypadNext}>Next →</button>}
      </div>

      {/* Digit grid */}
      <div style={st.keypadGrid}>
        {keys.map(k => (
          <button
            key={k}
            onClick={() => onInput(k)}
            style={{
              ...st.keypadKey,
              ...(k === "⌫" ? { color: "#dc2626" } : {}),
              ...(k === ".5" ? { color: "#6366f1", fontWeight: 700 } : {}),
            }}
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Add Customer Modal ────────────────────────────────────────────────────
function AddCustomerModal({ catalogueDefaults, hasCustomDefaults, vertical, onClose, onCreate }) {
  const [step, setStep]     = useState("choose");
  const [form, setForm]     = useState(emptyCustomer());
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const handleQuickSave = async () => {
    if (!form.name.trim() || !form.phone.trim()) { setError("Name and phone are required."); return; }
    setSaving(true); setError("");
    try   { await onCreate(form, "quick"); }
    catch (e) { setError(e.message || "Failed to create"); setSaving(false); }
  };

  if (step === "choose") return (
    <div style={st.modalBackdrop} onClick={onClose}>
      <div style={st.modalChoose} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 4 }}>New Customer</div>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 20 }}>How would you like to add them?</div>

        <button onClick={() => setStep("quick")} style={st.choiceBtn}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>⚡</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>Quick Add</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>Name + phone now → measurements next</div>
        </button>

        <button onClick={() => setStep("full")} style={st.choiceBtn}>
          <div style={{ fontSize: 24, marginBottom: 4 }}>📐</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>Full Profile</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>Everything in one go</div>
        </button>

        <button onClick={onClose} style={st.choiceCancel}>Cancel</button>
      </div>
    </div>
  );

  if (step === "quick") return (
    <div style={st.modalBackdrop} onClick={onClose}>
      <div style={st.modalQuick} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 6 }}>Quick Add</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>Save customer now, take measurements next.</div>

        <input autoFocus value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="Customer name" style={st.modalInput} />
        <input value={form.phone}
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          placeholder="Phone" inputMode="tel" style={st.modalInput} />

        <div style={{ marginBottom: 16 }}>
          <TagSelector
            tags={form.tags}
            onChange={tags => setForm(f => ({ ...f, tags }))}
          />
        </div>

        {error && (
          <div style={{ padding: "8px 12px", background: "#fff0f0", color: "#dc2626", fontSize: 13, borderRadius: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setStep("choose")} style={st.cancelBtn}>← Back</button>
          <button
            onClick={handleQuickSave}
            disabled={saving || !form.name.trim() || !form.phone.trim()}
            style={{ ...st.saveBtn, opacity: (saving || !form.name.trim() || !form.phone.trim()) ? 0.4 : 1 }}
          >
            {saving ? "Saving…" : "Save & Take Measurements →"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <CustomerDrawer
      customer={emptyCustomer()}
      catalogueDefaults={catalogueDefaults}
      hasCustomDefaults={hasCustomDefaults}
      vertical={vertical}
      onClose={() => setStep("choose")}
      onSave={async (c) => { await onCreate(c, "full"); }}
    />
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────
const st = {
  header:   { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 16px 12px" },
  title:    { fontSize: 18, fontWeight: 700, color: "#111" },
  subtitle: { fontSize: 12, color: "#888", marginTop: 2 },
  addBtn:   { background: "#111", color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  search:   { width: "100%", padding: "11px 14px", border: "1.5px solid #efefed", borderRadius: 12, fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fafaf8" },
  empty:    { padding: "40px 16px", textAlign: "center", color: "#aaa", fontSize: 14 },
  errorBar: { margin: "0 16px", padding: "10px 12px", background: "#fff0f0", color: "#dc2626", fontSize: 13, borderRadius: 10 },

  card:      { display: "flex", alignItems: "center", gap: 12, background: "#fff", margin: "0 16px 10px", borderRadius: 14, padding: "14px 16px", cursor: "pointer", border: "1px solid #f0f0f0" },
  avatar:    { width: 44, height: 44, borderRadius: "50%", background: "#fef3c7", color: "#92400e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, flexShrink: 0 },
  cardName:  { fontSize: 15, fontWeight: 700, color: "#111" },
  cardPhone: { fontSize: 13, color: "#666", marginTop: 2 },
  cardMeta:  { fontSize: 12, marginTop: 4 },
  tagChip: (tag) => ({
    fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10, textTransform: "capitalize",
    background: tag === "bridal" ? "#fce7f3" : tag === "premium" ? "#fef3c7" : tag === "new" ? "#dbeafe" : "#f0fdf4",
    color:      tag === "bridal" ? "#be185d" : tag === "premium" ? "#92400e" : tag === "new" ? "#1e40af" : "#166534",
  }),

  // Tag selector
  tagBtn: { padding: "5px 10px", borderRadius: 14, border: "1.5px solid #efefed", background: "#fff", fontSize: 12, fontWeight: 600, color: "#666", cursor: "pointer", textTransform: "capitalize" },
  tagBtnActive: (tag) => ({
    background: tag === "bridal" ? "#fce7f3" : tag === "premium" ? "#fef3c7" : tag === "new" ? "#dbeafe" : "#f0fdf4",
    color:      tag === "bridal" ? "#be185d" : tag === "premium" ? "#92400e" : tag === "new" ? "#1e40af" : "#166534",
    border: `1.5px solid ${tag === "bridal" ? "#fbcfe8" : tag === "premium" ? "#fde68a" : tag === "new" ? "#bfdbfe" : "#bbf7d0"}`,
  }),
  tagChipSelected: (tag) => ({
    fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 14, textTransform: "capitalize", cursor: "default",
    background: tag === "bridal" ? "#fce7f3" : tag === "premium" ? "#fef3c7" : tag === "new" ? "#dbeafe" : "#f0fdf4",
    color:      tag === "bridal" ? "#be185d" : tag === "premium" ? "#92400e" : tag === "new" ? "#1e40af" : "#166534",
    border: `1.5px solid ${tag === "bridal" ? "#fbcfe8" : tag === "premium" ? "#fde68a" : tag === "new" ? "#bfdbfe" : "#bbf7d0"}`,
  }),
  tagEditBtn: { padding: "5px 10px", borderRadius: 14, border: "1.5px solid #efefed", background: "#fff", fontSize: 11, fontWeight: 600, color: "#888", cursor: "pointer" },

  drawerBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  drawer:         { background: "#fff", width: "100%", maxWidth: 520, height: "94vh", borderTopLeftRadius: 20, borderTopRightRadius: 20, display: "flex", flexDirection: "column", overflow: "hidden" },
  drawerHeader:   { display: "flex", alignItems: "flex-start", gap: 10, padding: "16px 16px 12px", borderBottom: "1px solid #f0f0f0" },
  headerInput:    { width: "100%", border: "none", outline: "none", fontSize: 18, fontWeight: 700, color: "#111", padding: "2px 0", background: "transparent" },
  closeBtn:       { width: 32, height: 32, borderRadius: 8, border: "none", background: "#f5f5f3", fontSize: 14, color: "#666", cursor: "pointer", flexShrink: 0 },

  bannerEducational: { margin: "10px 16px", padding: "10px 12px", background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 10, fontSize: 12, color: "#92400e", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  bannerSuccess:     { margin: "10px 16px", padding: "10px 12px", background: "#eef2ff", border: "1.5px solid #c7d2fe", borderRadius: 10, fontSize: 12, color: "#4338ca", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  bannerDismiss:     { border: "none", background: "transparent", color: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: 2, opacity: 0.7, flexShrink: 0 },

  unitBtn:       { padding: "6px 14px", border: "none", background: "transparent", fontSize: 13, fontWeight: 600, color: "#888", cursor: "pointer", borderRadius: 8 },
  unitBtnActive: { background: "#fff", color: "#111", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" },
  undoBtn:       { padding: "7px 10px", borderRadius: 9, border: "1.5px solid #fde68a", background: "#fffbeb", fontSize: 12, fontWeight: 600, color: "#92400e", cursor: "pointer" },

  groupHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px 6px", cursor: "pointer", userSelect: "none" },
  fieldGrid:   { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "0 16px" },

  fieldCard:        { background: "#fff", border: "1.5px solid #efefed", borderRadius: 12, padding: "7px 4px 5px", userSelect: "none", WebkitUserSelect: "none" },
  fieldCardActive:  { border: "2px solid #6366f1", background: "#f5f3ff" },
  fieldCardFilled:  { background: "#f9fdf9", borderColor: "#bbf7d0" },
  fieldCardOutlier: { background: "#fffbeb", borderColor: "#fde68a" },

  fieldLabelInline: { fontSize: 11, fontWeight: 700, color: "#555", padding: "0 6px 2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // Row with [−] value [+] for filled cards
  fieldStepperRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 },
  cardStepBtn: {
    width: 40, height: 40, borderRadius: 10,
    border: "1px solid #e5e7eb", background: "#f9fafb",
    fontSize: 22, fontWeight: 700, color: "#374151",
    cursor: "pointer", flexShrink: 0,
    touchAction: "manipulation",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
  },
  fieldValue: { fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 },
  fieldUnit:  { fontSize: 10, fontWeight: 600, color: "#888" },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: "#444", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },

  textarea: { width: "100%", padding: "10px 12px", border: "1.5px solid #efefed", borderRadius: 10, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "vertical", background: "#fafaf8" },

  keypad:        { background: "#fafaf8", borderTop: "1px solid #efefed", padding: "8px 10px 10px", flexShrink: 0 },
  keypadHeader:  { padding: "0 4px 4px" },
  keypadActions: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, padding: "0 4px 6px" },
  adjustChip:    { width: 28, height: 28, borderRadius: 7, border: "1.5px solid #d1d5db", background: "#fff", fontSize: 15, fontWeight: 700, color: "#111", cursor: "pointer", flexShrink: 0, touchAction: "manipulation", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  keypadClear:   { padding: "5px 12px", borderRadius: 8, border: "1.5px solid #fecaca", background: "#fff0f0", fontSize: 12, fontWeight: 700, color: "#dc2626", cursor: "pointer" },
  keypadDone:    { padding: "5px 12px", borderRadius: 8, border: "1.5px solid #efefed", background: "#fff", fontSize: 12, fontWeight: 600, color: "#666", cursor: "pointer" },
  keypadNext:    { padding: "5px 12px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  keypadGrid:    { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 },
  keypadKey:     { padding: "11px 0", background: "#fff", border: "1.5px solid #efefed", borderRadius: 10, fontSize: 20, fontWeight: 700, color: "#111", cursor: "pointer", fontVariantNumeric: "tabular-nums", touchAction: "manipulation" },

  drawerFooter:  { display: "flex", gap: 8, padding: "10px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 },
  cancelBtn:     { padding: "11px 16px", border: "1.5px solid #efefed", background: "#fff", borderRadius: 11, fontSize: 13, fontWeight: 600, color: "#666", cursor: "pointer" },
  saveBtn:       { flex: 1, padding: "11px 16px", border: "none", background: "#111", borderRadius: 11, fontSize: 14, fontWeight: 700, color: "#fff", cursor: "pointer" },
  closeFullBtn:  { flex: 1, padding: "11px 16px", border: "1.5px solid #efefed", background: "#fff", borderRadius: 11, fontSize: 14, fontWeight: 700, color: "#444", cursor: "pointer" },

  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modalChoose:   { background: "#fff", borderRadius: 18, padding: 20, width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 10 },
  modalQuick:    { background: "#fff", borderRadius: 18, padding: 20, width: "100%", maxWidth: 400 },
  modalInput:    { width: "100%", padding: "11px 14px", border: "1.5px solid #efefed", borderRadius: 11, fontSize: 14, outline: "none", boxSizing: "border-box", background: "#fafaf8", marginBottom: 10 },
  choiceBtn:     { background: "#fff", border: "1.5px solid #efefed", borderRadius: 14, padding: "16px 14px", cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", alignItems: "flex-start" },
  choiceCancel:  { background: "transparent", border: "none", padding: "10px", marginTop: 4, fontSize: 13, color: "#888", cursor: "pointer" },
};
